import { some } from 'lodash';
import { useEffect, useState } from 'react';
import { setCurrentStepByHash } from '../../helpers/setCurrentStepByHash';
import { FormStep, Package, StepType, StepTypeWithIndex } from '../../types';
import { StepNavigator } from './StepNavigator';
import { usePreviousStep } from './usePreviousStep';
import { useStepHistory } from './useStepHistory';

type StepRestore = {
  step?: string;
  isReady: boolean;
  onRestored: () => void;
};

export const useStepNavigation = (
  rateCode: string,
  packageData: Package | undefined,
  allFormSteps: FormStep[],
  restore?: StepRestore
) => {
  const [currentStep, setCurrentStep] = useState<StepTypeWithIndex>({
    type: 'rooms',
    index: 0,
    step: 'guestDetailsStep',
  });
  const steps: StepType[] = ['rooms'];
  if (some(packageData?.experiences)) {
    steps.push('spa');
  }
  if (some(packageData?.restaurants)) {
    steps.push('dining');
  }

  const restoreStep = restore?.step;
  const isRestoreReady = !!restore?.isReady;

  useEffect(() => {
    if (!restoreStep || !isRestoreReady) return;
    setCurrentStepByHash(`#${restoreStep}`, allFormSteps, setCurrentStep);
    restore?.onRestored();
  }, [restoreStep, isRestoreReady]);

  useStepHistory(currentStep, setCurrentStep, allFormSteps, !!restoreStep);

  const isLastStep = currentStep.index === allFormSteps.length - 1;

  const currentFormStep = allFormSteps[currentStep.index] || allFormSteps[0];

  const goToNextStep = () => {
    setCurrentStep((val) => {
      const nextStep = allFormSteps[val.index + 1];
      return nextStep
        ? { type: nextStep.type as StepType, index: val.index + 1, step: nextStep.step }
        : {
            type: 'rooms',
            index: 0,
            step: 'guestDetailsStep',
          };
    });
  };

  const goToPreviousStep = usePreviousStep(rateCode, currentStep);

  return {
    stepNavigatorComponent: StepNavigator(rateCode, currentStep, steps),
    goToNextStep,
    goToPreviousStep,
    isLastStep,
    currentFormStep,
    currentStepIndex: currentStep.index,
  };
};
